import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { AlertTriangle, CheckCircle2, Loader2, Grape, Armchair } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Motion";
import { Seo } from "@/components/Seo";
import { api, fmtTime, fmtDate, formatApiError, useContent, track } from "@/lib/api";
import { EVENT_TYPES, OCCASIONS, HOLIDAYS } from "@/lib/eventOptions";

const SERVICES = ["Charcuterie boards", "Grazing table"];
const CHARCUTERIE_STYLES = ["Charcuterie boards", "Grazing table", "Boards + grazing table", "Not sure yet — help me decide"];
const FLEXIBILITY = ["Exact date only", "Flexible within a few days", "Flexible within a few weeks", "Fully flexible"];

const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const toHM = (mins) => `${String(Math.floor(mins / 60) % 24).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

export default function Inquire() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("type") === "charcuterie" ? "charcuterie" : "venue";
  const { data } = useContent();
  const s = data?.settings || {};
  const threshold = s.guest_threshold || 100;
  const minMins = (s.min_duration_hours || 2) * 60;

  const [form, setForm] = useState({
    name: "", email: "", phone: "", event_type: "", event_type_other: "",
    guest_count: "", date: searchParams.get("date") || "", start: "", end: "",
    undecided: false, flexibility: FLEXIBILITY[0], services: [],
    location: "", charcuterie_style: "", occasion: "", holiday: "",
    outside_vendors: "", outside_details: "", details: "", acknowledged: false,
  });
  const [clientToken] = useState(() => crypto.randomUUID());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => { track("inquiry_started"); }, []);

  const dayQ = useQuery({
    queryKey: ["inquire-day", form.date],
    queryFn: () => api.get("/public/availability/day", { params: { date: form.date } }).then((r) => r.data),
    enabled: mode === "venue" && !!form.date && !form.undecided,
    retry: 1,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startOptions = useMemo(() => {
    if (!dayQ.data?.free) return [];
    const opts = [];
    dayQ.data.free.forEach((f) => {
      const fs = toMin(f.start), fe = toMin(f.end) < fs ? toMin(f.end) + 1440 : toMin(f.end);
      for (let t = fs; t + minMins <= fe; t += 30) opts.push(t);
    });
    return opts;
  }, [dayQ.data, minMins]);

  const endOptions = useMemo(() => {
    if (form.start === "" || !dayQ.data?.free) return [];
    const start = Number(form.start);
    const range = dayQ.data.free.find((f) => {
      const fs = toMin(f.start), fe = toMin(f.end) < fs ? toMin(f.end) + 1440 : toMin(f.end);
      return fs <= start && start + minMins <= fe;
    });
    if (!range) return [];
    const fe = toMin(range.end) < toMin(range.start) ? toMin(range.end) + 1440 : toMin(range.end);
    const opts = [];
    for (let t = start + minMins; t <= fe; t += 30) opts.push(t);
    return opts;
  }, [form.start, dayQ.data, minMins]);

  const guestCount = Number(form.guest_count);
  const overCapacity = mode === "venue" && guestCount > threshold;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        client_token: clientToken,
        kind: mode,
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
        event_type: mode === "charcuterie" ? (form.occasion || "Charcuterie service") : form.event_type,
        event_type_other: mode === "venue" && form.event_type === "Other" ? form.event_type_other.trim() : null,
        holiday: mode === "charcuterie" && form.occasion === "Holiday party" ? form.holiday : null,
        guest_count: guestCount,
        date: form.undecided || !form.date ? null : form.date,
        start_time: mode === "venue" && !form.undecided && form.start !== "" ? toHM(Number(form.start)) : null,
        end_time: mode === "venue" && !form.undecided && form.end !== "" ? toHM(Number(form.end)) : null,
        date_flexible: form.undecided ? null : form.flexibility,
        location: mode === "charcuterie" ? form.location.trim() : null,
        charcuterie_style: mode === "charcuterie" ? form.charcuterie_style : null,
        services: mode === "venue" ? form.services : (form.charcuterie_style ? [form.charcuterie_style] : []),
        outside_vendors: mode === "venue" ? (form.outside_vendors || null) : null,
        details: [mode === "venue" && form.outside_details && `Outside vendors: ${form.outside_details}`, form.details].filter(Boolean).join("\n\n") || null,
        acknowledged: form.acknowledged,
      };
      const r = await api.post("/public/inquiries", payload);
      track("inquiry_submitted");
      setSuccess(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiError(err));
      if (err?.response?.status === 409 && form.date) dayQ.refetch();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-bone">
      <Seo title={mode === "charcuterie" ? "Charcuterie Inquiry — The Owl's Nest" : "Event Inquiry — The Owl's Nest"}
        description={mode === "charcuterie"
          ? "Charcuterie boards and grazing tables set up at your home, workplace, or venue by The Owl's Nest in Joshua, Texas."
          : "Tell us about your event at The Owl's Nest. An inquiry starts the conversation — it does not reserve your date."} />
      <Nav />
      <section className="mx-auto max-w-3xl px-4 pb-24 pt-32 sm:px-8 sm:pt-40">
        <Reveal>
          <p className="overline-label">{mode === "charcuterie" ? "Charcuterie Inquiry" : "Event Inquiry"}</p>
          <h1 className="mt-4 font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl">
            {mode === "charcuterie" ? "Charcuterie, delivered." : "Tell us about your event."}
          </h1>
          <p className="mt-5 text-base leading-relaxed text-stone-600">
            {mode === "charcuterie"
              ? "Boards and grazing tables, styled and set up at your home, workplace, or any venue — no room rental needed. Tell us the date and headcount and we'll quote it."
              : `${s.show_pricing && s.hourly_rate ? `Rentals are $${s.hourly_rate}/hour with a ${s.min_duration_hours}-hour minimum; charcuterie is quoted per event. ` : ""}An inquiry starts the conversation — it does not reserve your date.`}
          </p>
          <div className="mt-8 inline-flex rounded-full border border-black/10 bg-white p-1" role="tablist" data-testid="inquiry-mode-toggle">
            <Link to="/inquire" role="tab" aria-selected={mode === "venue"} data-testid="inquiry-mode-venue"
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold transition-colors ${mode === "venue" ? "bg-ink text-bone" : "text-stone-500 hover:text-ink"}`}>
              <Armchair className="h-4 w-4" /> Event space rental
            </Link>
            <Link to="/inquire?type=charcuterie" role="tab" aria-selected={mode === "charcuterie"} data-testid="inquiry-mode-charcuterie"
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold transition-colors ${mode === "charcuterie" ? "bg-ink text-bone" : "text-stone-500 hover:text-ink"}`}>
              <Grape className="h-4 w-4" /> Charcuterie only
            </Link>
          </div>
        </Reveal>

        {success ? (
          <Reveal className="card mt-12 p-10 text-center sm:p-14" data-testid="inquiry-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-green-700" strokeWidth={1.5} />
            <h2 className="mt-6 font-serif text-3xl text-ink">We&rsquo;ve got it.</h2>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-stone-600">{success.message}</p>
            <p className="mt-6 inline-block rounded-full bg-bone2 px-5 py-2.5 text-sm font-semibold tracking-wide text-ink" data-testid="inquiry-reference">
              Reference {success.reference}
            </p>
            <div className="mt-10">
              <Link to="/" className="btn-ghost !px-6 !py-3 text-xs">Back to home</Link>
            </div>
          </Reveal>
        ) : (
          <form onSubmit={submit} className="mt-12 space-y-12" data-testid="inquiry-form">
            <Reveal className="card p-6 sm:p-10">
              <p className="overline-label">01 · {mode === "charcuterie" ? "Your setup" : "Event details"}</p>
              <div className="mt-6 grid gap-6 sm:grid-cols-2">
                {mode === "venue" && (
                  <div>
                    <label htmlFor="event-type" className="label">Event type *</label>
                    <select id="event-type" required value={form.event_type} data-testid="inquiry-select-event-type"
                      onChange={(e) => set("event_type", e.target.value)} className="field">
                      <option value="">Choose one…</option>
                      {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                )}
                {mode === "venue" && form.event_type === "Other" && (
                  <div>
                    <label htmlFor="event-other" className="label">Tell us what it is *</label>
                    <input id="event-other" required value={form.event_type_other} data-testid="inquiry-input-event-other"
                      onChange={(e) => set("event_type_other", e.target.value)} className="field" placeholder="e.g. Book launch" />
                  </div>
                )}
                {mode === "charcuterie" && (
                  <div>
                    <label htmlFor="charcuterie-style" className="label">What are you thinking? *</label>
                    <select id="charcuterie-style" required value={form.charcuterie_style} data-testid="inquiry-select-charcuterie-style"
                      onChange={(e) => set("charcuterie_style", e.target.value)} className="field">
                      <option value="">Choose one…</option>
                      {CHARCUTERIE_STYLES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                )}
                {mode === "charcuterie" && (
                  <div>
                    <label htmlFor="occasion" className="label">What's the occasion? *</label>
                    <select id="occasion" required value={form.occasion} data-testid="inquiry-select-occasion"
                      onChange={(e) => setForm((f) => ({ ...f, occasion: e.target.value, holiday: "" }))} className="field">
                      <option value="">Choose one…</option>
                      {OCCASIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                )}
                {mode === "charcuterie" && form.occasion === "Holiday party" && (
                  <div>
                    <label htmlFor="holiday" className="label">Which holiday? *</label>
                    <select id="holiday" required value={form.holiday} data-testid="inquiry-select-holiday"
                      onChange={(e) => set("holiday", e.target.value)} className="field">
                      <option value="">Choose one…</option>
                      {HOLIDAYS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                )}
                {mode === "charcuterie" && (
                  <div>
                    <label htmlFor="location" className="label">Where should we set up? *</label>
                    <input id="location" required value={form.location} data-testid="inquiry-input-location"
                      onChange={(e) => set("location", e.target.value)} className="field"
                      placeholder="Home, workplace, another venue — with city" />
                  </div>
                )}
                <div>
                  <label htmlFor="guest-count" className="label">{mode === "charcuterie" ? "How many people?" : "Estimated guests *"}</label>
                  <input id="guest-count" type="number" min="1" required value={form.guest_count} data-testid="inquiry-input-guest-count"
                    onChange={(e) => set("guest_count", e.target.value)} className="field" placeholder="e.g. 60" />
                  {overCapacity && (
                    <p className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 p-3.5 text-xs leading-relaxed text-amber-800 ring-1 ring-amber-600/20" data-testid="inquiry-guest-warning">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      Our room comfortably seats about {threshold}. Send the inquiry anyway — tell us more below and we&rsquo;ll talk through options.
                    </p>
                  )}
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex cursor-pointer items-center gap-3 text-sm text-stone-700">
                    <input type="checkbox" checked={form.undecided} data-testid="inquiry-checkbox-undecided"
                      onChange={(e) => setForm((f) => ({ ...f, undecided: e.target.checked, date: "", start: "", end: "" }))}
                      className="h-4 w-4 rounded border-stone-300 accent-[#1C1917]" />
                    {mode === "charcuterie" ? "Our date isn't decided yet" : "Our date isn\u2019t decided yet"}
                  </label>
                </div>
              </div>

              {!form.undecided && (
                <div className="mt-6 grid gap-6 border-t border-black/5 pt-6 sm:grid-cols-3">
                  <div>
                    <label htmlFor="event-date" className="label">{mode === "charcuterie" ? "Preferred date" : "Preferred date"}</label>
                    <input id="event-date" type="date" value={form.date} min={format(new Date(), "yyyy-MM-dd")}
                      data-testid="inquiry-input-date"
                      onChange={(e) => setForm((f) => ({ ...f, date: e.target.value, start: "", end: "" }))} className="field" />
                  </div>
                  {mode === "venue" && (
                    <>
                      <div>
                        <label htmlFor="event-start" className="label">Start time</label>
                        <select id="event-start" value={form.start} disabled={!form.date || dayQ.isLoading || startOptions.length === 0}
                          data-testid="inquiry-select-start"
                          onChange={(e) => setForm((f) => ({ ...f, start: e.target.value, end: "" }))} className="field disabled:opacity-50">
                          <option value="">{!form.date ? "Pick a date first" : dayQ.isLoading ? "Loading…" : startOptions.length ? "Choose…" : "No open times"}</option>
                          {startOptions.map((t) => <option key={t} value={t}>{fmtTime(toHM(t))}</option>)}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="event-end" className="label">End time</label>
                        <select id="event-end" value={form.end} disabled={form.start === ""}
                          data-testid="inquiry-select-end"
                          onChange={(e) => set("end", e.target.value)} className="field disabled:opacity-50">
                          <option value="">{form.start === "" ? "Pick a start" : `Choose (min ${s.min_duration_hours || 2} hrs)…`}</option>
                          {endOptions.map((t) => <option key={t} value={t}>{fmtTime(toHM(t))}</option>)}
                        </select>
                      </div>
                    </>
                  )}
                  {mode === "venue" && form.date && dayQ.data && (dayQ.data.status === "unavailable" || dayQ.data.status === "closed" || dayQ.data.status === "past") && (
                    <p className="text-xs text-red-700 sm:col-span-3" data-testid="inquiry-date-unavailable">
                      {fmtDate(form.date)} is fully booked. <Link className="underline" to="/availability">See open dates</Link> — or check &ldquo;date not decided&rdquo;.
                    </p>
                  )}
                  <div className="sm:col-span-3">
                    <label htmlFor="flexibility" className="label">How flexible is the date?</label>
                    <select id="flexibility" value={form.flexibility} onChange={(e) => set("flexibility", e.target.value)}
                      data-testid="inquiry-select-flexibility" className="field sm:max-w-xs">
                      {FLEXIBILITY.map((f) => <option key={f}>{f}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </Reveal>

            {mode === "venue" && (
              <Reveal className="card p-6 sm:p-10">
                <p className="overline-label">02 · Charcuterie <span className="normal-case tracking-normal text-stone-400">(optional — quoted per event)</span></p>
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {SERVICES.map((svc) => (
                    <label key={svc} className="flex cursor-pointer items-center gap-3 rounded-xl border border-black/5 bg-bone px-4 py-3 text-sm text-stone-700 transition-colors hover:border-black/15">
                      <input type="checkbox" checked={form.services.includes(svc)} data-testid={`inquiry-service-${svc.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        onChange={(e) => set("services", e.target.checked ? [...form.services, svc] : form.services.filter((x) => x !== svc))}
                        className="h-4 w-4 rounded accent-[#1C1917]" />
                      {svc}
                    </label>
                  ))}
                </div>
                <div className="mt-6">
                  <label htmlFor="outside-vendors" className="label">Will you bring outside vendors?</label>
                  <select id="outside-vendors" value={form.outside_vendors} data-testid="inquiry-select-vendors"
                    onChange={(e) => set("outside_vendors", e.target.value)} className="field sm:max-w-xs">
                    <option value="">Not sure yet</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {form.outside_vendors === "Yes" && (
                    <textarea value={form.outside_details} onChange={(e) => set("outside_details", e.target.value)}
                      data-testid="inquiry-input-vendor-details" rows={2} className="field mt-4"
                      placeholder="Who are you thinking of? (florist, DJ, caterer…)" aria-label="Outside vendor details" />
                  )}
                </div>
              </Reveal>
            )}

            <Reveal className="card p-6 sm:p-10">
              <p className="overline-label">{mode === "charcuterie" ? "02" : "03"} · Your details</p>
              <div className="mt-6 grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="label">Your name *</label>
                  <input id="name" required value={form.name} data-testid="inquiry-input-name"
                    onChange={(e) => set("name", e.target.value)} className="field" placeholder="Full name" autoComplete="name" />
                </div>
                <div>
                  <label htmlFor="phone" className="label">Phone *</label>
                  <input id="phone" type="tel" required value={form.phone} data-testid="inquiry-input-phone"
                    onChange={(e) => set("phone", e.target.value)} className="field" placeholder="(817) 526-5388" autoComplete="tel" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="email" className="label">Email *</label>
                  <input id="email" type="email" required value={form.email} data-testid="inquiry-input-email"
                    onChange={(e) => set("email", e.target.value)} className="field" placeholder="you@example.com" autoComplete="email" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="details" className="label">Anything else we should know?</label>
                  <textarea id="details" rows={4} value={form.details} data-testid="inquiry-input-details"
                    onChange={(e) => set("details", e.target.value)} className="field"
                    placeholder={mode === "charcuterie" ? "The occasion, dietary needs, setup timing…" : "The occasion, the vibe, timing questions, accessibility needs…"} />
                </div>
              </div>
              <label className="mt-8 flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-stone-700">
                <input type="checkbox" required checked={form.acknowledged} data-testid="inquiry-checkbox-ack"
                  onChange={(e) => set("acknowledged", e.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 rounded accent-[#1C1917]" />
                <span>
                  I understand this inquiry does not reserve my date, and that availability is confirmed
                  personally by The Owl&rsquo;s Nest. *
                </span>
              </label>
            </Reveal>

            {error && (
              <p className="rounded-xl bg-red-50 p-4 text-sm text-red-800 ring-1 ring-red-700/15" role="alert" data-testid="inquiry-error">
                {error}
              </p>
            )}

            <button type="submit" disabled={submitting} data-testid="inquiry-submit-button"
              className="btn-dark w-full !py-4 text-base disabled:opacity-60">
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "charcuterie" ? "Send Charcuterie Inquiry" : "Send Inquiry"}
            </button>
            <p className="text-center text-xs text-stone-400">
              We reply personally, usually within a day or two. Your details are only used to respond to this inquiry.
            </p>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}
